import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute, notFound, useRouter} from "@tanstack/react-router";
import type {CSSProperties} from "react";
import {useCallback, useMemo, useState} from "react";
import {JSONTree} from "react-json-tree";
import {twJoin} from "tailwind-merge";
import {AnankeTempPageTitle} from "~/components/anankeTemp/anankeTempPageTitle";
import {AnankeTempVerticalSpacer} from "~/components/anankeTemp/anankeTempVerticalSpacer";
import {Breadcrumbs} from "~/components/breadcrumbs";
import {DebuggingPanel} from "~/components/debuggingPanel";
import {
    EventDetailsColumns,
    type EventColumnGroup,
} from "~/components/eventDetails/eventDetailColumns";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {OttopilotEventViewPanelSectionKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTableViewColumn";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {useAnankePreferences} from "@vesta/ananke/contexts/anankeClientContext";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import {getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {getEventsWithEventContainers, updateEvent, type EventWithEventContainer} from "~/utilities/apis/api";
import {uniqueUuid} from "@vesta/unique/unique";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {amaronUpdateEventAction} from "~/utilities/apis/amaronActionsApi";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/event-containers/$eventContainerId/events/$eventId",
)({
    loader: async ({context, params}) => {
        const eventContainerIdResult = uuidZodValidator.safeParse(params.eventContainerId);
        if (eventContainerIdResult.success !== true) {
            throw notFound();
        }
        const eventContainerId = eventContainerIdResult.data;

        const eventIdResult = uuidZodValidator.safeParse(params.eventId);
        if (eventIdResult.success !== true) {
            throw notFound();
        }
        const eventId = eventIdResult.data;

        const eventsResult = await getEventsWithEventContainers({
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            filterConfiguration: {
                revisionId: uniqueUuid`98beb352-d956-4e82-ae53-658ed0b2bb01`,
                lineItems: [
                    {
                        columnId: "id",
                        operation: FilterOperation.equals,
                        value: eventId,
                    },
                ],
            },
            sortConfiguration: {
                revisionId: uniqueUuid`28d308a5-2796-4d9e-a731-6603fc523bcb`,
                lineItems: [],
            },
            limit: 2,
            offset: 0,
            workspaceId: context.workspace.id,
        });
        if (eventsResult.success !== true) {
            throw eventsResult.error;
        }
        const events = eventsResult.data;
        if (events.length === 0) {
            throw notFound();
        }
        if (events.length > 1) {
            throw new Error("Constraint voilated: Multiple events found for the same ID");
        }
        const event = events[0]!;
        if (event.eventContainer.id !== eventContainerId) {
            throw new Error("Constraint voilated: Mismatched eventContainerId");
        }

        const eventContainerEventsResult = await getEventsWithEventContainers({
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            filterConfiguration: {
                revisionId: uniqueUuid`11beb352-d956-4e82-ae53-658ed0b2bb03`,
                lineItems: [
                    {
                        columnId: "eventContainerId",
                        operation: FilterOperation.equals,
                        value: eventContainerId,
                    },
                ],
            },
            sortConfiguration: {
                revisionId: uniqueUuid`22ceb352-d956-4e82-ae53-658ed0b2bb04`,
                lineItems: [],
            },
            limit: 500,
            offset: 0,
            workspaceId: context.workspace.id,
        });

        const linkedEventContainerEventTemplateIds: Array<Uuid> =
            eventContainerEventsResult.success === true
                ? eventContainerEventsResult.data.map((eventEntry) => eventEntry.templateId)
                : [];

        return {event, linkedEventContainerEventTemplateIds};
    },
    component: RouteComponent,
});

function RouteComponent() {
    const {event, linkedEventContainerEventTemplateIds} = Route.useLoaderData();

    const {workspace} = useWorkspace();
    const eventTemplates = useEventTemplates();
    const eventContainerTemplate = useRequiredEventContainerTemplate(event.eventContainer.templateId);

    const eventTemplate = eventTemplates.find((eventTemplate) => eventTemplate.id === event.templateId);
    if (eventTemplate === undefined) {
        throw new Error("Event template not found");
    }
    return (
        <div className="w-full h-full p-ds-space-200 sm:p-ds-space-300 relative">
            <Breadcrumbs
                items={[
                    {
                        displayName: eventContainerTemplate.configuration.moduleDisplayNameVariants.pluralSentenceCase,
                        to: `/w/${workspace.id}/ottopilot/event-containers`,
                    },
                    {
                        displayName: `${eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase} profile`,
                        to: `/w/${workspace.id}/ottopilot/event-containers/${event.eventContainer.id}`,
                    },
                    {
                        displayName: "Event details",
                        to: null,
                    },
                ]}
            />

            <div className="grid grid-flow-col justify-between gap-2 items-start">
                <AnankeTempPageTitle title={eventTemplate.displayName} />

                {/* <div className="grid w-max grid-flow-col gap-2">
                    <AnankeLinkButton href={`/w/${workspace.id}/ottopilot/events/create?eventContainerId=${journey.id}`}>
                        + Add event
                    </AnankeLinkButton>

                    <AnankeTempDropdownMenuIconButtonTrigger
                        icon={ShowMoreHorizontalIcon}
                        iconLabel=""
                    >
                        <AnankeDropdownItemGroup>
                            <AnankeDropdownItem
                                onClick={() => {
                                    setIsAuditLogsModalOpened(true);
                                }}
                            >
                                View logs
                            </AnankeDropdownItem>
                            <AnankeDropdownItem
                                onClick={() => {
                                    setIsDeleteModalOpened(true);
                                }}
                            >
                                Delete
                            </AnankeDropdownItem>
                        </AnankeDropdownItemGroup>
                    </AnankeTempDropdownMenuIconButtonTrigger>
                </div> */}
            </div>

            <VerticalSpacer className="h-4" />

            <DetailsTab
                event={event}
                eventTemplate={eventTemplate}
                linkedJourneyEventTemplateIds={linkedEventContainerEventTemplateIds}
            />

            {/* <JSONTree
                data={event}
                hideRoot={true}
                shouldExpandNodeInitially={() => true}
                theme="chalk"
                invertTheme={anankePreferences.theme === "dark" ? false : true}
            /> */}
        </div>
    );
}

// function DetailsTab({
//     event,
//     eventTemplate,
//     className,
//     style,
// }: {
//     event: EventWithEventContainer;
//     eventTemplate: OttopilotEventTemplate;
//     className?: string;
//     style?: CSSProperties;
//     //
// }) {
//     const {heimdallUser} = useWorkspaceUser();
//     const {intellsysBaseUrl} = useOttopilotConfiguration();
//     const {workspace, workspaceCapabilities} = useWorkspace();
//     const anankePreferences = useAnankePreferences();

//     const navigate = useNavigate();
//     const router = useRouter();
//     const anankeFlags = useAnankeFlags();

//     const [diff, setDiff] = useState<
//         Partial<Event> & {
//             additionalColumnValues: Record<Uuid, unknown>;
//         }
//     >({
//         additionalColumnValues: {},
//     });

//     const eventWithDiff = {
//         ...event,
//         ...diff,
//         additionalColumnValues: {
//             ...event.additionalColumnValues,
//             ...diff.additionalColumnValues,
//         },
//     };

//     const updateEventMutation = useMutation({
//         mutationKey: [uniqueUuid`28f2f1d7-90d1-4f6f-892c-205fb10a990b`],
//         mutationFn: async () => {
//             const updateEventResult = await updateEvent({
//                 accessToken: heimdallUser.accessToken,
//                 intellsysBaseUrl: intellsysBaseUrl,
//                 eventId: event.id,
//                 templateId: eventTemplate.id,
//                 data: {
//                     ...diff.additionalColumnValues,
//                 },
//                 workspaceId: workspace.id,
//             });
//             if (updateEventResult.success !== true) {
//                 throw updateEventResult.error;
//             }
//         },
//     });

//     return (
//         <div
//             className={twJoin("w-full grid grid-cols-1", className)}
//             style={style}
//         >
//             <DebuggingPanel>
//                 <JSONTree
//                     data={diff}
//                     hideRoot={true}
//                     shouldExpandNodeInitially={() => false}
//                     theme="chalk"
//                     invertTheme={anankePreferences.theme === "dark" ? false : true}
//                 />
//             </DebuggingPanel>

//             <ItemBuilder
//                 items={eventTemplate.additionalColumns}
//                 itemBuilder={(column) => (
//                     <div
//                         className="grid grid-flow-col justify-start gap-4 px-ds-space-150 py-ds-space-100"
//                         key={column.id}
//                     >
//                         <div
//                             className="w-[8.6875rem] sm:w-[15.1875rem] font-ds-font-weight-medium leading-ds-space-250 text-[0.875rem] p-ds-space-100"
//                             style={{
//                                 color: token("color.text"),
//                                 font: "Inter",
//                             }}
//                             id={column.id}
//                             key={column.id}
//                         >
//                             <div className="w-full">{column.displayName}</div>
//                         </div>
//                         <div className="sm:w-[17.625rem]">
//                             {eventAdditionalColumnTypeToDetailsMap[column.type].inlineEditComponent({
//                                 value: eventWithDiff.additionalColumnValues[column.id] as any,
//                                 options:
//                                     eventTemplate.additionalColumns.find((item) => item.id === column.id)?.configuration
//                                         ?.options ?? [],
//                                 defaultValue: {
//                                     label: (() => {
//                                         const foundColumn = eventTemplate.additionalColumns.find(
//                                             (col) => col.id === column.id,
//                                         );
//                                         if (foundColumn?.configuration && "options" in foundColumn.configuration) {
//                                             const options = (
//                                                 foundColumn.configuration as {
//                                                     options: Array<{
//                                                         id: Uuid;
//                                                         displayName: string;
//                                                     }>;
//                                                 }
//                                             ).options;
//                                             const matchedOption = Object.values(options).find(
//                                                 (item: any) =>
//                                                     item.id === eventWithDiff.additionalColumnValues[column.id],
//                                             );
//                                             return matchedOption?.displayName ?? "-";
//                                         }
//                                         return "Select an option";
//                                     })(),
//                                     value: eventWithDiff.additionalColumnValues[column.id],
//                                 },
//                                 setValue: (nextState) => {
//                                     if (nextState === eventWithDiff.additionalColumnValues[column.id]) {
//                                         return;
//                                     }
//                                     setDiff((prev) => ({
//                                         ...prev,
//                                         additionalColumnValues: {
//                                             ...prev.additionalColumnValues,
//                                             [column.id]: nextState,
//                                         },
//                                     }));
//                                     updateEventMutation.mutate(undefined, {
//                                         onSuccess: () => {
//                                             anankeFlags.showFlag({
//                                                 title: "Event updated successfully",
//                                                 appearance: "success",
//                                                 isAutoDismiss: true,
//                                             });
//                                             // TODO: Refetch the event properly
//                                             for (const kvp of Object.entries(diff.additionalColumnValues)) {
//                                                 event.additionalColumnValues[kvp[0] as any] = kvp[1];
//                                             }
//                                             event.updatedAt = getCurrentIsoTimestamp();
//                                             event.updatedBy = heimdallUser.id;
//                                             setDiff({
//                                                 additionalColumnValues: {},
//                                             });
//                                             router.invalidate();
//                                             // navigate({
//                                             //     to: `/w/${workspace.id}/ottopilot/event-containers/${event.eventContainerId}/events/${event.id}`,
//                                             // });
//                                         },
//                                         onError: (error) => {
//                                             anankeFlags.showFlag({
//                                                 title: "Failed to update event",
//                                                 description: error.message,
//                                                 appearance: "error",
//                                                 isAutoDismiss: true,
//                                             });
//                                         },
//                                     });
//                                 },
//                             })}
//                         </div>
//                     </div>
//                     // <AnankeField
//                     //     id={column.id}
//                     //     label={column.displayName}
//                     //     // description={`(Debugging) Type: ${eventAdditionalColumnTypeToDetailsMap[column.type].displayName}`}
//                     //     key={column.id}
//                     // >
//                     //     {eventAdditionalColumnTypeToDetailsMap[column.type].inlineEditComponent({
//                     //         value: eventWithDiff.additionalColumnValues[column.id] as any,
//                     //         options:
//                     //             eventTemplate.additionalColumns.find((item) => item.id === column.id)?.configuration
//                     //                 ?.options ?? [],
//                     //         defaultValue: {
//                     //             label: (() => {
//                     //                 const foundColumn = eventTemplate.additionalColumns.find(
//                     //                     (col) => col.id === column.id,
//                     //                 );
//                     //                 if (foundColumn?.configuration && "options" in foundColumn.configuration) {
//                     //                     const options = (
//                     //                         foundColumn.configuration as {
//                     //                             options: Array<{
//                     //                                 id: Uuid;
//                     //                                 displayName: string;
//                     //                             }>;
//                     //                         }
//                     //                     ).options;
//                     //                     const matchedOption = Object.values(options).find(
//                     //                         (item: any) => item.id === eventWithDiff.additionalColumnValues[column.id],
//                     //                     );
//                     //                     return matchedOption?.displayName ?? "-";
//                     //                 }
//                     //                 return "Select an option";
//                     //             })(),
//                     //             value: eventWithDiff.additionalColumnValues[column.id],
//                     //         },
//                     //         setValue: (nextState) => {
//                     //             setDiff((prev) => ({
//                     //                 ...prev,
//                     //                 additionalColumnValues: {
//                     //                     ...prev.additionalColumnValues,
//                     //                     [column.id]: nextState,
//                     //                 },
//                     //             }));
//                     //         },
//                     //     })}
//                     // </AnankeField>
//                 )}
//                 spaceBuilder={(spaceIndex) => (
//                     <AnankeTempVerticalSpacer
//                         above="formRow"
//                         below="formRow"
//                         key={spaceIndex}
//                     />
//                 )}
//             />

//             <AnankeTempVerticalSpacer
//                 above="collapsibleSectionEnd"
//                 below="actions"
//             />

//             {/* <div className="w-full grid grid-cols-[auto] justify-end">
//                 <AnankeButton
//                     appearance="primary"
//                     onClick={() => {
//                         updateEventMutation.mutate(undefined, {
//                             onSuccess: () => {
//                                 anankeFlags.showFlag({
//                                     title: "Event updated successfully",
//                                     appearance: "success",
//                                     isAutoDismiss: true,
//                                 });
//                                 // TODO: Refetch the event properly
//                                 for (const kvp of Object.entries(diff.additionalColumnValues)) {
//                                     event.additionalColumnValues[kvp[0] as any] = kvp[1];
//                                 }
//                                 event.updatedAt = getCurrentIsoTimestamp();
//                                 event.updatedBy = heimdallUser.id;
//                                 setDiff({
//                                     additionalColumnValues: {},
//                                 });
//                                 router.invalidate();
//                                 // navigate({
//                                 //     to: `/w/${workspace.id}/ottopilot/event-containers/${event.eventContainerId}/events/${event.id}`,
//                                 // });
//                             },
//                             onError: (error) => {
//                                 anankeFlags.showFlag({
//                                     title: "Failed to update event",
//                                     description: error.message,
//                                     appearance: "error",
//                                     isAutoDismiss: true,
//                                 });
//                             },
//                         });
//                     }}
//                     isLoading={updateEventMutation.isPending}
//                     isDisabled={Object.keys(diff).length === 1 && Object.keys(diff.additionalColumnValues).length === 0}
//                 >
//                     Save
//                 </AnankeButton>
//             </div> */}
//         </div>
//     );
// }

function DetailsTab({
    event,
    eventTemplate,
    linkedJourneyEventTemplateIds,
    className,
    style,
}: {
    event: EventWithEventContainer;
    eventTemplate: OttopilotEventTemplate;
    linkedJourneyEventTemplateIds: Array<Uuid>;
    className?: string;
    style?: CSSProperties;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const anankePreferences = useAnankePreferences();
    const eventTemplates = useEventTemplates();
    const eventContainerTemplate = useRequiredEventContainerTemplate(event.eventContainer.templateId);

    const columnGrouping = useMemo<Array<EventColumnGroup>>(() => {
        const editableByKey = new Map<string, boolean>();
        const editPanel = eventTemplate.configuration.eventPage.editPanel;
        if (editPanel !== undefined) {
            for (const group of editPanel.columnGrouping) {
                for (const col of group.columns) {
                    const eventPart = col.columnType === "OttopilotEvent" ? (col.eventTemplateId ?? eventTemplate.id) : "";
                    const key = `${col.columnType}:${String(col.columnId)}:${eventPart}`;
                    editableByKey.set(key, col.isEditable);
                }
            }
        }

        const resolveEditable = (col: {
            columnType: "OttopilotEvent" | "OttopilotEventContainer";
            columnId: Uuid | string;
            eventTemplateId?: Uuid;
        }): boolean => {
            const eventPart = col.columnType === "OttopilotEvent" ? (col.eventTemplateId ?? eventTemplate.id) : "";
            const key = `${col.columnType}:${String(col.columnId)}:${eventPart}`;
            return editableByKey.get(key) ?? false;
        };

        const viewPanel = eventTemplate.configuration.eventPage.viewPanel;
        if (viewPanel !== undefined) {
            return viewPanel.sections.map((section) => {
                if (section.kind === OttopilotEventViewPanelSectionKind.eventOverview) {
                    return {
                        kind: OttopilotEventViewPanelSectionKind.eventOverview,
                        id: section.id,
                        displayName: section.displayName,
                        initialIsOpen: section.initialIsOpen,
                        className: section.className,
                    };
                }
                return {
                    id: section.id,
                    displayName: section.displayName,
                    columns: section.columns.map((col) => ({
                        id: col.id,
                        label: col.label,
                        columnId: col.columnId,
                        columnType: col.columnType,
                        eventTemplateId: col.columnType === "OttopilotEvent" ? (col.eventTemplateId ?? eventTemplate.id) : undefined,
                        isAdditionalColumn: col.isAdditionalColumn,
                        isEditable: resolveEditable(col),
                    })),
                };
            });
        }
        return [
            {
                id: `${eventTemplate.id}-group` as Uuid,
                displayName: `${eventTemplate.displayName} Details`,
                columns: getAdditionalColumns(eventTemplate).map((col) => ({
                    id: col.id,
                    columnId: col.id,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                    isEditable: resolveEditable({
                        columnType: "OttopilotEvent",
                        columnId: col.id,
                        eventTemplateId: eventTemplate.id,
                    }),
                    label: col.displayName,
                })),
            },
        ];
    }, [getAdditionalColumns, eventTemplate]);

    const router = useRouter();
    const anankeFlags = useAnankeFlags();

    const [diff, setDiff] = useState<{
        eventAdditionalColumnValues: Record<Uuid, unknown>;
        eventContainerAdditionalColumnValues: Record<Uuid, unknown>;
    }>({
        eventAdditionalColumnValues: {},
        eventContainerAdditionalColumnValues: {},
    });

    const resetEdits = useCallback(() => {
        setDiff({
            eventAdditionalColumnValues: {},
            eventContainerAdditionalColumnValues: {},
        });
    }, []);

    // Guard against navigating away with un-persisted inline edits (e.g. a failed save).
    const isDirty =
        Object.keys(diff.eventAdditionalColumnValues).length > 0
        || Object.keys(diff.eventContainerAdditionalColumnValues).length > 0;
    const {modal, allowNavigation} = useUnsavedChangesGuard({isDirty, onDiscard: resetEdits});

    const eventWithDiff: EventWithEventContainer = {
        ...event,
        additionalColumnValues: {
            ...event.additionalColumnValues,
            ...diff.eventAdditionalColumnValues,
        },
        eventContainer: {
            ...event.eventContainer,
            additionalColumnValues: {
                ...event.eventContainer.additionalColumnValues,
                ...diff.eventContainerAdditionalColumnValues,
            },
        },
    };

    const updateMutation = useMutation({
        mutationKey: [uniqueUuid`28f2f1d7-90d1-4f6f-892c-205fb10a990b`],
        mutationFn: async () => {
            if (isAmaronWorkspace(workspaceCapabilities) === true && Object.keys(diff.eventAdditionalColumnValues).length > 0) {
                const updateEventResult = await amaronUpdateEventAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        eventId: event.id,
                        eventPatch: {
                            additionalColumnValues: diff.eventAdditionalColumnValues,
                        },
                        ...(Object.keys(diff.eventContainerAdditionalColumnValues).length > 0
                            ? {
                                eventContainerPatch: {
                                    additionalColumnValues: diff.eventContainerAdditionalColumnValues,
                                },
                            }
                            : {}),
                        templateId: eventTemplate.id,
                        workspaceId: workspace.id,
                    },
                });
                if (updateEventResult.success !== true) {
                    throw updateEventResult.error;
                }
                return;
            }

            // Update event if there are event column changes
            if (Object.keys(diff.eventAdditionalColumnValues).length > 0) {
                const updateEventResult = await updateEvent({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    eventId: event.id,
                    templateId: eventTemplate.id,
                    data: diff.eventAdditionalColumnValues as never,
                    workspaceId: workspace.id,
                });
                if (updateEventResult.success !== true) {
                    throw updateEventResult.error;
                }
            }

            // Update the parent Event Container if there are parent-column changes.
            if (Object.keys(diff.eventContainerAdditionalColumnValues).length > 0) {
                const updateEventContainerResult = await updateEventContainer({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    id: event.eventContainer.id,
                    templateId: event.eventContainer.templateId,
                    data: diff.eventContainerAdditionalColumnValues,
                    workspaceId: workspace.id,
                });
                if (updateEventContainerResult.success !== true) {
                    throw updateEventContainerResult.error;
                }
            }
        },
    });

    return (
        <>
        {modal}
        <div
            className={twJoin("w-full grid grid-cols-1", className)}
            style={style}
        >
            <DebuggingPanel>
                <JSONTree
                    data={diff}
                    hideRoot={true}
                    shouldExpandNodeInitially={() => false}
                    theme="chalk"
                    invertTheme={anankePreferences.theme === "dark" ? false : true}
                />
            </DebuggingPanel>

            <EventDetailsColumns
                columnGrouping={columnGrouping}
                journeysModuleConfiguration={eventContainerTemplate}
                eventTemplates={eventTemplates}
                eventWithDiff={eventWithDiff}
                event={event}
                linkedJourneyEventTemplateIds={linkedJourneyEventTemplateIds}
                onColumnValueChange={(columnId, nextState, eventTemplateId) => {
                    if (eventTemplateId !== undefined) {
                        // Event column change
                        if (nextState === event.additionalColumnValues[columnId as Uuid]) {
                            return;
                        }
                        setDiff((prev) => ({
                            ...prev,
                            eventAdditionalColumnValues: {
                                ...prev.eventAdditionalColumnValues,
                                [columnId]: nextState,
                            },
                        }));
                    } else {
                        // Event Container column change
                        if (nextState === event.eventContainer.additionalColumnValues[columnId as Uuid]) {
                            return;
                        }
                        setDiff((prev) => ({
                            ...prev,
                            eventContainerAdditionalColumnValues: {
                                ...prev.eventContainerAdditionalColumnValues,
                                [columnId]: nextState,
                            },
                        }));
                    }
                    updateMutation.mutate(undefined, {
                        onSuccess: () => {
                            allowNavigation(() => {
                                anankeFlags.showFlag({
                                    title: "Updated successfully",
                                    appearance: "success",
                                    isAutoDismiss: true,
                                });
                                // Apply changes to original objects
                                for (const [key, value] of Object.entries(diff.eventAdditionalColumnValues)) {
                                    event.additionalColumnValues[key as Uuid] = value;
                                }
                                for (const [key, value] of Object.entries(diff.eventContainerAdditionalColumnValues)) {
                                    event.eventContainer.additionalColumnValues[key as Uuid] = value;
                                }
                                event.updatedAt = getCurrentIsoTimestamp();
                                event.updatedBy = heimdallUser.id;
                                setDiff({
                                    eventAdditionalColumnValues: {},
                                    eventContainerAdditionalColumnValues: {},
                                });
                                router.invalidate();
                            });
                        },
                        onError: (error) => {
                            anankeFlags.showFlag({
                                title: "Failed to update",
                                description: error.message,
                                appearance: "error",
                                isAutoDismiss: true,
                            });
                        },
                    });
                }}
            />

            <AnankeTempVerticalSpacer
                above="collapsibleSectionEnd"
                below="actions"
            />

            {/* <div className="w-full grid grid-cols-[auto] justify-end">
                <AnankeButton
                    appearance="primary"
                    onClick={() => {
                        updateEventMutation.mutate(undefined, {
                            onSuccess: () => {
                                anankeFlags.showFlag({
                                    title: "Event updated successfully",
                                    appearance: "success",
                                    isAutoDismiss: true,
                                });
                                // TODO: Refetch the event properly
                                for (const kvp of Object.entries(diff.additionalColumnValues)) {
                                    event.additionalColumnValues[kvp[0] as any] = kvp[1];
                                }
                                event.updatedAt = getCurrentIsoTimestamp();
                                event.updatedBy = heimdallUser.id;
                                setDiff({
                                    additionalColumnValues: {},
                                });
                                router.invalidate();
                                // navigate({
                                //     to: `/w/${workspace.id}/ottopilot/event-containers/${event.eventContainerId}/events/${event.id}`,
                                // });
                            },
                            onError: (error) => {
                                anankeFlags.showFlag({
                                    title: "Failed to update event",
                                    description: error.message,
                                    appearance: "error",
                                    isAutoDismiss: true,
                                });
                            },
                        });
                    }}
                    isLoading={updateEventMutation.isPending}
                    isDisabled={Object.keys(diff).length === 1 && Object.keys(diff.additionalColumnValues).length === 0}
                >
                    Save
                </AnankeButton>
            </div> */}
        </div>
        </>
    );
}
